import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import {
  Menu,
  Radio,
  Clock,
  Router,
  Sun,
  Moon,
  MonitorCog
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export const Navbar = ({ onMenuClick }) => {
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const [timeStr, setTimeStr] = useState("");
  const [net, setNet] = useState({ routers_online: 0, total_routers: 0, olts_online: 0, total_olts: 0 });

  const THEME_CYCLE = { dark: "light", light: "system", system: "dark" };
  const THEME_META = {
    dark: { label: "Mode Gelap", Icon: Moon },
    light: { label: "Mode Terang", Icon: Sun },
    system: { label: "Mode Sistem", Icon: MonitorCog }
  };
  const CurrentThemeIcon = THEME_META[theme].Icon;

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setTimeStr(
        now.toLocaleTimeString("id-ID", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          timeZoneName: "short"
        })
      );
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchNet = async () => {
      try {
        const [dash, olts] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/dashboard/stats`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/jaringan/olt/devices`, { withCredentials: true })
        ]);
        const n = dash.data?.network || {};
        const oltList = olts.data || [];
        setNet({
          routers_online: n.routers_online || 0,
          total_routers: n.total_routers || 0,
          olts_online: oltList.filter((o) => o.last_status === "online").length,
          total_olts: oltList.length
        });
      } catch (err) {
        // diam saja, badge menampilkan angka terakhir
      }
    };
    fetchNet();
    const t = setInterval(fetchNet, 60000);
    return () => clearInterval(t);
  }, []);

  return (
    <header className="h-16 bg-[#090f1d]/90 backdrop-blur-md border-b border-slate-800 px-4 sm:px-6 flex items-center justify-between sticky top-0 z-30">
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuClick}
          data-testid="mobile-menu-toggle"
          className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 lg:hidden"
        >
          <Menu className="w-5 h-5" />
        </button>

        <div className="hidden sm:flex items-center gap-2 text-xs text-slate-400 bg-slate-900/60 border border-slate-800 px-3 py-1.5 rounded-full" data-testid="navbar-router-status">
          <span className={`w-2 h-2 rounded-full ${net.routers_online > 0 ? "bg-emerald-400 animate-pulse" : "bg-slate-500"}`}></span>
          <Router className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-slate-300 font-medium">Router:</span>
          <span className={`font-mono font-semibold ${net.routers_online > 0 ? "text-emerald-400" : "text-slate-400"}`}>
            {net.routers_online}/{net.total_routers} Online
          </span>
        </div>
      </div>

      <div className="flex items-center gap-4">
        {/* Live Clock WIB */}
        <div className="hidden md:flex items-center gap-1.5 text-xs text-slate-300 font-mono bg-slate-900/80 px-3 py-1.5 rounded-lg border border-slate-800">
          <Clock className="w-3.5 h-3.5 text-cyan-400" />
          <span>{timeStr}</span>
        </div>

        {/* OLT Status Badge (data rill) */}
        <div className="hidden lg:flex items-center gap-1.5 text-xs bg-slate-900/80 border border-slate-800 px-3 py-1.5 rounded-lg" data-testid="navbar-olt-status">
          <Radio className={`w-3.5 h-3.5 ${net.olts_online > 0 ? "text-blue-400" : "text-slate-500"}`} />
          <span className="text-slate-400">OLT:</span>
          <span className={`font-semibold ${net.olts_online > 0 ? "text-cyan-300" : "text-slate-400"}`}>
            {net.total_olts === 0 ? "Belum terdaftar" : `${net.olts_online}/${net.total_olts} Online`}
          </span>
        </div>

        {/* Theme Toggle */}
        <button
          onClick={() => setTheme(THEME_CYCLE[theme])}
          data-testid="theme-toggle"
          title={`Tema: ${THEME_META[theme].label} — klik untuk ganti`}
          aria-label={`Tema saat ini: ${THEME_META[theme].label}`}
          className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 border border-slate-800 transition-colors"
        >
          <CurrentThemeIcon className="w-4 h-4" />
        </button>

        {/* User Info Badge */}
        <div className="flex items-center gap-2 pl-2 border-l border-slate-800">
          <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-cyan-600 to-blue-600 flex items-center justify-center text-white text-xs font-bold shadow">
            {user?.name?.[0] || "A"}
          </div>
          <div className="hidden sm:block text-left">
            <p className="text-xs font-medium text-slate-200">{user?.name || "Admin"}</p>
            <p className="text-[10px] text-slate-400 capitalize">{user?.role || "Staff"}</p>
          </div>
        </div>
      </div>
    </header>
  );
};
